import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Button, Dialog } from '../index';

import { useTheme, type Theme } from './theme';

// The question the logger asks when a program-prescribed AMRAP set is completed
// without the chip (LG-26). Chrome from the shared <Dialog>, actions from the
// shared <Button> — same composition as its sibling FailConfirmModal, which is
// the other prompt that fires on a completion gesture and changes what gets
// written.
//
// Both answers are buttons, and neither is preselected. There is no primary-by
// -default here in the "safe choice" sense: "yes" writes a measurement and "no"
// writes nothing, and the app has no business nudging toward either. What the
// primary fill marks is the answer that carries information, not the answer we
// want. Dismissing (back / backdrop) reads as "no" — the direction that records
// nothing, because a prompt that recorded a limit on a stray tap would be worse
// than no prompt at all.
type Props = {
  /** Yes — the set went to the limit. */
  onConfirm: () => void;
  /** No, back, or backdrop. */
  onDismiss: () => void;
  /** Reps as logged. */
  reps?: string;
  /** Prescribed floor, when the set carries one. */
  floor?: string;
};

export function AmrapConfirmModal({
  onConfirm,
  onDismiss,
  reps,
  floor,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = makeStyles(theme);
  // A set logged at (or under) the floor is the shape of the false positive:
  // the athlete did what was asked, stopped, and is now being asked whether
  // that was failure (LG-28, Antenore's smoke 2026-07-24). It is not proof of
  // anything — stopping exactly at 8 when 8 was your true limit is a real and
  // common outcome — so it does not change the answers on offer. It changes
  // what the athlete is told before choosing: the two numbers, side by side.
  //
  // ⚠️ The blank guard is load-bearing, not defensive noise: `Number('')` is
  // 0, and `Number.isFinite(0)` is true, so an empty reps cell would sail
  // through the comparison and render "You logged , and the plan asked for at
  // least 8" — a broken sentence at the exact moment the athlete decides.
  const trimmedReps = reps?.trim() ?? '';
  const trimmedFloor = floor?.trim() ?? '';
  const atFloor =
    trimmedReps !== '' &&
    trimmedFloor !== '' &&
    Number.isFinite(Number(trimmedReps)) &&
    Number.isFinite(Number(trimmedFloor)) &&
    Number(trimmedReps) <= Number(trimmedFloor);

  return (
    <Dialog
      theme={theme}
      visible
      onRequestClose={onDismiss}
      dismissOnBackdropPress
      title={t('mobile.logger.amrapConfirmTitle', {
        defaultValue: 'Did you go to failure?',
      })}
    >
      {/* One line, not three. It is read between sets, standing at a machine,
          and the "your program asked for AMRAP" half is already on screen as
          the badge next to Reps — repeating it here would cost a read and add
          nothing. What is NOT on screen anywhere is what separates a hard set
          from a measurement, so that is the line that stays. */}
      <Text style={styles.prompt}>
        {t('mobile.logger.amrapConfirmBody', {
          defaultValue:
            'Answer yes only if the next rep would not have come up.',
        })}
      </Text>
      {atFloor ? (
        <Text style={styles.atFloor}>
          {t('mobile.logger.amrapConfirmAtFloor', {
            defaultValue:
              'You logged {{reps}}, and the plan asked for at least {{floor}}.',
            reps,
            floor,
          })}
        </Text>
      ) : null}
      <View style={styles.actions}>
        {/* The facing edges carry NO touch slop. <Button> defaults to 8 on all
            four sides, which in a column is 8 + 8 reaching across the gap: a
            band where the two hit regions overlap and the tap resolves to
            whichever wins. On any other pair that is a mis-tap; here one answer
            writes a measurement and the other writes nothing, decided one-handed
            between sets. With the facing slop at 0 the seam belongs to neither
            button, so a tap that lands there does nothing — the only outcome
            that cannot be wrong. (Button's own doc-comment calls out the row
            case; this is its column twin.) */}
        <Button
          theme={theme}
          variant="secondary"
          hitSlop={{ top: 8, left: 8, right: 8, bottom: 0 }}
          label={t('mobile.logger.amrapConfirmNo', {
            defaultValue: 'No, I had more in me',
          })}
          onPress={onDismiss}
        />
        <Button
          theme={theme}
          variant="primary"
          hitSlop={{ top: 0, left: 8, right: 8, bottom: 8 }}
          label={t('mobile.logger.amrapConfirmYes', {
            defaultValue: 'Yes, to failure',
          })}
          onPress={onConfirm}
        />
      </View>
    </Dialog>
  );
}

const makeStyles = (theme: Theme) =>
  StyleSheet.create({
    prompt: {
      color: theme.palette.ink,
      fontSize: theme.typography.sizes.base,
    },
    // Secondary ink, not a warning colour: this is context, not an accusation,
    // and the tone rule is that a coach says what to do next, not what you got
    // wrong (measurement-contract §11.5). Never colour-only — it is a whole
    // sentence, so it survives with no colour at all.
    atFloor: {
      marginTop: theme.spacing.s2,
      color: theme.palette.ink2,
      // `base`, matching the question above it, NOT a smaller footnote tier:
      // this line carries the two numbers the yes/no answer hinges on, and the
      // reduced-vision floor is a readability rule, not a hierarchy style.
      fontSize: theme.typography.sizes.base,
    },
    // Column, not row: the two labels are sentences, and side by side they wrap
    // to two lines each at 280dp (the a11y floor this app is held to) where the
    // yes/no distinction has to survive on the text, never on position or fill
    // alone.
    actions: {
      marginTop: theme.spacing.s4,
      // s4, not s2: two answers that mean opposite things need to read as two
      // choices, not a stack, and the wider gap also keeps the seam above from
      // being a 4dp sliver.
      gap: theme.spacing.s4,
    },
  });
